import { useCallback, useEffect, useMemo, useRef, useState, ComponentType, ReactNode } from 'react'
import { connect } from 'react-redux'
import { AnimatePresence, motion } from 'framer-motion'
import { Filter, ChevronDown, ArrowUpRight, X } from 'lucide-react'

import {
  FilterPanel,
  FilterSection,
  FilterField,
  FilterFieldButton,
  FilterDropdown,
  FilterDropdownItem,
} from '../common/FilterPanel.tsx'
import styles from './FilterPrivateProjects.module.scss'

import { getSubquotationType, filterPrivateProjects, getSkills } from '../../../store/actions'

type Option = {
  id: number
  name: string
}

const Presence = AnimatePresence as unknown as ComponentType<{ children: ReactNode; initial?: boolean }>

type FilterPrivateProjectsProps = {
  filterPrivateProjects: (filter: any, page: number, pageSize: number, reset?: boolean) => void
  getSubquotationType: () => void
  getSkills: () => void
  setIsFiltered?: (value: boolean) => void
  setIsAdjudicated?: (value: boolean) => void
  resultados: number
  totalDeProyectosMostrados: number
  listaDeRubros: Option[]
  currentPage: number
}

const DEFAULT_PAGE_SIZE = 9

const dateOptions = ['Próximos 30 días', '30 a 60 días', '60 a 90 días'] as const
const sortOptions = ['Recientes', 'Antiguos'] as const

const FilterPrivateProjects = ({
  filterPrivateProjects: dispatchFilter,
  getSubquotationType: fetchSubquotationType,
  getSkills: fetchSkills,
  setIsFiltered,
  setIsAdjudicated,
  resultados,
  totalDeProyectosMostrados,
  listaDeRubros,
  currentPage,
}: FilterPrivateProjectsProps) => {
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null)
  const [selectedWorkType, setSelectedWorkType] = useState<Option | null>(null)
  const [selectedDateRange, setSelectedDateRange] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [zone, setZone] = useState('')
  const [showExactDateInput, setShowExactDateInput] = useState(false)
  const [selectedSort, setSelectedSort] = useState<typeof sortOptions[number]>('Recientes')
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false)

  const mobileFilterRef = useRef<HTMLDivElement | null>(null)

  const filterCount = useMemo(() => {
    let count = 0
    if (selectedWorkType !== null) count++
    if (selectedDateRange || selectedDate) count++
    if (zone.trim() !== '') count++
    if (selectedSort !== 'Recientes') count++
    return count
  }, [selectedDate, selectedDateRange, selectedSort, selectedWorkType, zone])

  const markFiltered = useCallback(
    (value: boolean) => {
      if (typeof setIsFiltered === 'function') {
        setIsFiltered(value)
      }
    },
    [setIsFiltered],
  )

  const markAdjudicated = useCallback(
    (value: boolean) => {
      if (typeof setIsAdjudicated === 'function') {
        setIsAdjudicated(value)
      }
    },
    [setIsAdjudicated],
  )

  const formatDate = (value: string) => {
    const date = new Date(value)
    const day = String(date.getUTCDate()).padStart(2, '0')
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const year = date.getUTCFullYear()
    return `${day}/${month}/${year}`
  }

  const handleFilterClick = (filterName: string) => {
    setSelectedFilter((prev) => (prev === filterName ? null : filterName))
  }

  const generateDateRange = (label: string | null) => {
    if (!label) return null
    const today = new Date()
    const clone = (days: number) => {
      const next = new Date(today)
      next.setDate(today.getDate() + days)
      return next
    }

    switch (label) {
      case 'Próximos 30 días':
        return [today, clone(30)]
      case '30 a 60 días':
        return [clone(30), clone(60)]
      case '60 a 90 días':
        return [clone(60), clone(90)]
      default:
        return null
    }
  }

  const buildFilterPayload = useCallback(() => {
    const toISO = (date: Date) => {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }

    const range = generateDateRange(selectedDateRange)
    const dateFrom = selectedDate ? toISO(new Date()) : range ? toISO(range[0]) : null
    const dateTo = selectedDate ? selectedDate : range ? toISO(range[1]) : null

    return {
      sort: selectedSort === 'Recientes' ? 'DESC' : 'ASC',
      zone: zone || null,
      workType: selectedWorkType ? selectedWorkType.name : null,
      dateRange: { dateFrom, dateTo },
    }
  }, [selectedDate, selectedDateRange, selectedSort, selectedWorkType, zone])

  const handleFilter = useCallback(() => {
    markFiltered(true)
    markAdjudicated(false)
    const payload = buildFilterPayload()
    dispatchFilter(payload, currentPage, DEFAULT_PAGE_SIZE)
  }, [buildFilterPayload, currentPage, dispatchFilter, markAdjudicated, markFiltered])

  const handleReset = () => {
    setSelectedWorkType(null)
    setSelectedDateRange(null)
    setSelectedDate(null)
    setZone('')
    setSelectedSort('Recientes')
    setSelectedFilter(null)
    markFiltered(false)
  }

  const handleMobileOpen = () => {
    setMobileFilterOpen(true)
    document.body.style.overflow = 'hidden'
  }

  const handleMobileClose = () => {
    setMobileFilterOpen(false)
    document.body.style.overflow = ''
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileFilterRef.current && !mobileFilterRef.current.contains(event.target as Node)) {
        handleMobileClose()
      }
    }

    if (mobileFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [mobileFilterOpen])

  useEffect(() => {
    fetchSkills()
    fetchSubquotationType()
  }, [fetchSkills, fetchSubquotationType])

  const handleWorkTypeSelect = (option: Option | null) => {
    setSelectedWorkType(option)
    setSelectedFilter(null)
  }

  useEffect(() => {
    handleFilter()
  }, [handleFilter, selectedWorkType, selectedDateRange, selectedDate, zone, selectedSort])

  const summary = (
    <div className={styles.summaryRow}>
      <p className={styles.summaryText}>
        {resultados === 0 ? (
          'No se encontraron resultados'
        ) : resultados === 1 ? (
          <>
            Se encontró <span className={styles.summaryHighlight}>1</span> resultado
          </>
        ) : (
          <>
            Se encontraron <span className={styles.summaryHighlight}>{resultados}</span> resultados
          </>
        )}
        {resultados > DEFAULT_PAGE_SIZE && (
          <span className={styles.summaryExtra}>
            · Mostrando {totalDeProyectosMostrados} de {resultados}
          </span>
        )}
      </p>
      <div className={styles.sortControl}>
        <span className={styles.sortLabel}>Ordenar por</span>
        <button
          type="button"
          className={styles.sortButton}
          onClick={() => setSelectedSort((prev) => (prev === 'Recientes' ? 'Antiguos' : 'Recientes'))}
        >
          {selectedSort}
          <ChevronDown size={16} />
        </button>
      </div>
    </div>
  )

  const workTypeOptions: Option[] = listaDeRubros ?? []

  const primary = (
    <>
      <FilterSection>
        <FilterField>
          <FilterFieldButton
            title="Rubro principal"
            value={selectedWorkType?.name}
            placeholder="Todos"
            onClick={() => handleFilterClick('workType')}
            active={selectedFilter === 'workType'}
          />
          {selectedFilter === 'workType' && (
            <FilterDropdown>
              <FilterDropdownItem
                key="all"
                onSelect={() => handleWorkTypeSelect(null)}
                active={selectedWorkType === null}
              >
                Todos
              </FilterDropdownItem>
              {workTypeOptions.map((option) => (
                <FilterDropdownItem
                  key={option.id}
                  onSelect={() => handleWorkTypeSelect({ id: option.id, name: option.name })}
                  active={selectedWorkType?.id === option.id}
                >
                  {option.name}
                </FilterDropdownItem>
              ))}
            </FilterDropdown>
          )}
        </FilterField>
      </FilterSection>

      <FilterSection>
        <FilterField>
          <FilterFieldButton
            title="Fecha de apertura"
            value={selectedDateRange || (selectedDate ? formatDate(selectedDate) : undefined)}
            placeholder="Elegir"
            onClick={() => handleFilterClick('date')}
            active={selectedFilter === 'date'}
          />
          {selectedFilter === 'date' && (
            <FilterDropdown>
              {dateOptions.map((option) => (
                <FilterDropdownItem
                  key={option}
                  onSelect={() => {
                    setSelectedDateRange(option)
                    setSelectedDate(null)
                    setShowExactDateInput(false)
                    setSelectedFilter(null)
                  }}
                  active={selectedDateRange === option}
                >
                  {option}
                </FilterDropdownItem>
              ))}
              <div className={styles.dropdownFooter}>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => {
                    setSelectedDateRange(null)
                    setShowExactDateInput(true)
                  }}
                >
                  Fecha exacta
                </button>
                {showExactDateInput && (
                  <input
                    type="date"
                    className={styles.dateInput}
                    value={selectedDate ?? ''}
                    onChange={(event) => setSelectedDate(event.target.value)}
                  />
                )}
              </div>
            </FilterDropdown>
          )}
        </FilterField>
      </FilterSection>

      <FilterSection>
        <input
          type="text"
          className={styles.textInput}
          value={zone}
          onChange={(event) => setZone(event.target.value)}
          placeholder="Ej. Córdoba, Patagonia, Buenos Aires..."
        />
      </FilterSection>
    </>
  )

  const renderDesktop = () => (
    <FilterPanel
      title="Proyectos privados"
      filterCount={filterCount}
      onReset={handleReset}
      onToggleAdvanced={() => setSelectedFilter((prev) => (prev === 'sort' ? null : 'sort'))}
      advancedOpen={false}
      summary={summary}
      primary={primary}
    />
  )

  const renderMobile = () => (
    <>
      <div className={styles.mobileStickyControls}>
        <button type="button" className={styles.mobileTrigger} onClick={handleMobileOpen}>
          <Filter size={18} />
          Filtros
          {filterCount > 0 && <span className={styles.mobileBadge}>{filterCount}</span>}
        </button>
        <button
          type="button"
          className={styles.mobileSortButton}
          onClick={() => setSelectedSort((prev) => (prev === 'Recientes' ? 'Antiguos' : 'Recientes'))}
        >
          {selectedSort}
          <ChevronDown size={16} />
        </button>
      </div>

      <Presence initial={false}>
        {mobileFilterOpen && (
          <motion.div
            className={styles.mobileOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              ref={mobileFilterRef}
              className={styles.mobileSheet}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
            >
              <header className={styles.mobileHeader}>
                <div>
                  <h3>Filtros</h3>
                  <p>{resultados} resultados</p>
                </div>
                <button type="button" className={styles.mobileClose} onClick={handleMobileClose}>
                  <X size={20} />
                </button>
              </header>

              <div className={styles.mobileContent}>
                <FilterFieldButton
                  title="Rubro principal"
                  value={selectedWorkType?.name}
                  placeholder="Todos"
                  onClick={() => handleFilterClick('workType-mobile')}
                  active={selectedFilter === 'workType-mobile'}
                />
                <Presence initial={false}>
                  {selectedFilter === 'workType-mobile' && (
                    <motion.div
                      key="workType-mobile-dropdown"
                      className={styles.mobileDropdown}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.24, ease: 'easeOut' }}
                      style={{ overflow: 'hidden' }}
                    >
                      <FilterDropdownItem
                        key="all-option"
                        onSelect={() => {
                          handleWorkTypeSelect(null)
                          setSelectedFilter(null)
                        }}
                        active={selectedWorkType === null}
                      >
                        Todos
                      </FilterDropdownItem>
                      {workTypeOptions.map((option) => (
                        <FilterDropdownItem
                          key={option.id}
                          onSelect={() => {
                            handleWorkTypeSelect({ id: option.id, name: option.name })
                            setSelectedFilter(null)
                          }}
                          active={selectedWorkType?.id === option.id}
                        >
                          {option.name}
                        </FilterDropdownItem>
                      ))}
                    </motion.div>
                  )}
                </Presence>

                <FilterFieldButton
                  title="Fecha de apertura"
                  value={selectedDateRange || (selectedDate ? formatDate(selectedDate) : undefined)}
                  placeholder="Elegir"
                  onClick={() => handleFilterClick('date-mobile')}
                  active={selectedFilter === 'date-mobile'}
                />
                <Presence initial={false}>
                  {selectedFilter === 'date-mobile' && (
                    <motion.div
                      key="date-mobile-dropdown"
                      className={styles.mobileDropdown}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.24, ease: 'easeOut' }}
                      style={{ overflow: 'hidden' }}
                    >
                      {dateOptions.map((option) => (
                        <FilterDropdownItem
                          key={option}
                          onSelect={() => {
                            setSelectedDateRange(option)
                            setSelectedDate(null)
                            setShowExactDateInput(false)
                            setSelectedFilter(null)
                          }}
                          active={selectedDateRange === option}
                        >
                          {option}
                        </FilterDropdownItem>
                      ))}
                      <div className={styles.dropdownFooter}>
                        <button
                          type="button"
                          className={styles.linkButton}
                          onClick={() => {
                            setSelectedDateRange(null)
                            setShowExactDateInput(true)
                          }}
                        >
                          Fecha exacta
                        </button>
                        {showExactDateInput && (
                          <input
                            type="date"
                            className={styles.dateInput}
                            value={selectedDate ?? ''}
                            onChange={(event) => setSelectedDate(event.target.value)}
                          />
                        )}
                      </div>
                    </motion.div>
                  )}
                </Presence>

                <input
                  type="text"
                  className={styles.textInput}
                  value={zone}
                  onChange={(event) => setZone(event.target.value)}
                  placeholder="Ubicación"
                />
              </div>

              <footer className={styles.mobileFooter}>
                <button type="button" className={styles.mobileReset} onClick={handleReset}>
                  Limpiar filtros
                </button>
                <button type="button" className={styles.mobileApply} onClick={handleMobileClose}>
                  Aplicar
                  <ArrowUpRight size={18} />
                </button>
              </footer>
            </motion.div>
          </motion.div>
        )}
      </Presence>
    </>
  )

  return (
    <div className={styles.root}>
      <div className={styles.desktop}>{renderDesktop()}</div>
      <div className={styles.mobile}>{renderMobile()}</div>
    </div>
  )
}

const mapStateToProps = (state: any) => ({
  resultados: state.dashboard.itemCount,
  totalDeProyectosMostrados: state.dashboard.allProjects?.length ?? 0,
  listaDeRubros: state.register.skills ?? [],
})

const mapDispatchToProps = {
  getSubquotationType,
  filterPrivateProjects,
  getSkills,
}

export default connect(mapStateToProps, mapDispatchToProps)(FilterPrivateProjects)
